/** Audiences & Access (/w/:ws/audiences/:tab?). Audience names always come from tenant labels. */
export const audiences = {
  title: "Audiences & Access",
  subtitleLead: "Decide who can see what. Documents and answers are filtered by audience",
  subtitleBefore: "before",
  subtitleTail: "the AI sees them, so a public visitor can never get an answer from a restricted file.",
  testAs: "Test as an audience",
  explainerTitle: "How access filtering works",
  explainerBody:
    "When someone asks a question, Sagot checks who they are (anonymous, verified contact, signed-in member, staff), then searches only the knowledge sources their audience can access. If nothing matches, the agent says it can't help and offers a handoff. It never guesses from restricted files.",
  tabsLabel: "Audiences & Access sections",
  tabAudiences: "Audiences",
  tabMatrix: "Access matrix",
  tabUsers: "End users",
  tabIdp: "Identity providers",
  default: "Default",
  noDescription: "No description yet",
  endUsersUnit: "end users",
  verifiedBy: "Verified by",
  canAccess: "Can access",
  everythingPublic: "Everything public",
  allSources_one: "All {{count}} source",
  allSources_other: "All {{count}} sources",
  moreSources_one: "+{{count}} more",
  moreSources_other: "+{{count}} more",
  noSources: "No sources yet",
  agentsLine: "Agents: {{agents}}",
  noAgents: "No agents use this audience yet",
  edit: "Edit",
  editNamed: "Edit {{name}}",
  editAccess: "Edit access",
  viewAccess: "View access",
  emptyTitle: "No audiences yet",
  emptyBody: "Audiences appear here once the workspace is set up.",
  editTitle: "Edit “{{name}}”",
  editHint: "The name shows up in the access matrix, the knowledge base and agent settings.",
  labelField: "Name",
  labelRequired: "Give the audience a name",
  descriptionField: "Description",
  descriptionHint: "Who belongs to this audience, e.g. “Anyone on our website”.",
  methodField: "How are they verified?",
  methodUnset: "Not set",
  saved: "“{{name}}” saved",
  methods: {
    anonymous: "Anonymous",
    widget_jwt: "Signed widget token (JWT)",
    portal_sso: "Portal sign-in (JWT)",
    google_workspace: "Google Workspace",
    microsoft_entra: "Microsoft Entra ID",
    email_otp: "Email code",
    roster: "Roster ID check",
  },
  matrixHint: "Check a box to let an audience's questions be answered from that source. Higher audiences don't inherit automatically.",
  matrixSave: "Save access",
  matrixSaved: "Access saved. Answers are filtered with the new rules.",
  matrixAffected_one: "{{count}} agent picks up the change on its next answer.",
  matrixAffected_other: "{{count}} agents pick up the change on their next answer.",
  matrixSource: "Knowledge source",
  matrixCell: "{{source}} for {{audience}}",
  matrixLast: "Every source needs at least one audience",
  matrixFailed: "Processing failed — keep it restricted until it's fixed",
  matrixInherits: "Inherits audiences from its agents",
  matrixNoteRestricted: "Restricted sources are never sent to the model for other audiences.",
  matrixNoteOcr: "Low OCR confidence: keep restricted until a clearer copy is uploaded.",
  matrixEmptyTitle: "No knowledge sources yet",
  matrixEmptyBody: "Add documents, websites or Q&As to an agent, then decide here who can see them.",
  matrixEmptyCta: "Go to agents",
  usersHint: "People who chat with your agents · {{total}} total",
  usersSearch: "Search name, ID, email…",
  usersAudienceFilter: "Filter by audience",
  allAudiences: "All audiences",
  importCsv: "Import CSV",
  importHint: "Columns: {{columns}} (audiences like 1|3).",
  importTitle: "Import finished",
  importCreated: "Created",
  importUpdated: "Updated",
  importSkipped: "Skipped",
  importErrors_one: "{{count}} row needs fixing",
  importErrors_other: "{{count}} rows need fixing",
  importRow: "Row {{row}}",
  importClean: "Every row was imported.",
  colName: "Name",
  colRole: "Role",
  colSegment: "Group",
  colAudience: "Audience",
  colLastActive: "Last active",
  colConversations: "Conversations",
  colStatus: "Status",
  colActions: "Actions",
  anonymousUser: "Unnamed visitor",
  usersEmptyTitle: "No end users yet",
  usersEmptyBody: "End users appear when people verify in a chat, or import them from a CSV ({{columns}}).",
  usersNoMatch: "No end users match these filters",
  usersShowing: "Showing {{shown}} of {{total}}",
  paginationNav: "End users pages",
  previousPage: "Previous page",
  nextPage: "Next page",
  pageN: "Page {{page}}",
  pageOf: "Page {{page}} of {{count}}",
  status: {
    active: "Active",
    verified: "Verified",
    invited: "Invited",
    blocked: "Blocked",
  },
  userActions: "End user actions",
  userActionsFor: "Actions for {{name}}",
  viewConversations: "View conversations",
  changeAudience: "Change audience",
  changeAudienceHint: "Public is always included. Takes effect on their next message.",
  saveAudience: "Save audience",
  audienceChanged: "Audience changed. Takes effect on their next message.",
  block: "Block user",
  unblock: "Unblock user",
  blocked: "{{name}} is blocked from all agents",
  unblocked: "{{name}} can chat again",
  idp: {
    connected: "Connected",
    notConnected: "Not connected",
    off: "Off",
    enable: "Accept sign-ins from this provider",
    enableNamed: "Enable {{name}}",
    enabledToast: "{{name}} enabled",
    disabledToast: "{{name}} disabled",
    configure: "Configure",
    connect: "Connect",
    disconnect: "Disconnect",
    rosterReadOnly: "Managed by the roster import. Turn it on or off here.",
    configureTitle: "Configure {{name}}",
    connectTitle: "Connect {{type}}",
    saved: "{{name}} saved",
    deleted: "{{name}} disconnected",
    deleteTitle: "Disconnect {{name}}?",
    deleteBody: "People verified through it lose access to restricted answers until they verify another way. This can't be undone.",
    name: "Display name",
    nameRequired: "Give the provider a name",
    issuer: "Issuer (iss)",
    audience: "Audience (aud)",
    algorithm: "Algorithm",
    jwksUrl: "JWKS URL",
    sharedSecret: "Shared secret",
    secretHint: "Write-only. Leave blank to keep the current secret.",
    claimMapping: "Claim mapping",
    claim: "Claim {{n}}",
    claimValue: "Claim {{n}} value",
    mapsTo: "Row {{n}} audience",
    removeRow: "Remove row {{n}}",
    addClaim: "Add claim",
    groupMapping: "Group → audience",
    group: "Group {{n}}",
    addGroup: "Add group",
    pickAudience: "Choose audience",
    grants: "Maps to audience",
    grantsHint: "Verified people get these audiences (plus public).",
    mapsToNone: "Public only",
    lastVerified: "Last verified token",
    never: "Never",
    domain: "Allowed domain",
    tenantId: "Tenant ID",
    clientId: "Application (client) ID",
    clientSecret: "Client secret",
    redirectUri: "Redirect URI",
    redirectHint: "Add this in Azure portal → App registrations → Authentication.",
    redirectAfterSave: "The redirect URI appears here after you connect.",
    signExample: "Example: sign a token in your portal (Node.js)",
    requiredDomain: "Google Workspace needs the allowed domain.",
    requiredEntra: "Microsoft Entra ID needs the tenant ID and application (client) ID.",
    requiredPortal: "Portal sign-in needs the issuer and a JWKS URL or shared secret.",
    types: {
      portal_sso: "Portal sign-in (JWT)",
      widget_jwt: "Signed widget token",
      google_workspace: "Google Workspace",
      microsoft_entra: "Microsoft Entra ID",
      email_otp: "Email code",
      roster: "Roster ID check",
    },
    descriptions: {
      portal_sso: "Your portal signs a token with the member's ID and role. The widget and web app pass it on every message.",
      widget_jwt: "Your site signs a short-lived token for the widget so signed-in visitors get their audience.",
      google_workspace: "Staff sign in with their Google account. Only addresses on the allowed domain are accepted.",
      microsoft_entra: "For organizations on Microsoft 365. Map Entra groups to audiences.",
      email_otp: "People confirm a one-time code sent to their email address.",
      roster: "People enter their ID plus a second factor, checked against the imported roster. IDs are hashed at rest and never shown to the AI model.",
    },
  },
} as const;
